// ===========================================
// Generate Invoices Dialog
// ===========================================
// Dialog for bulk generating term invoices

import { useEffect, useMemo, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Box,
  Typography,
  Alert,
  Chip,
  FormControlLabel,
  Checkbox,
  Switch,
  Grid,
  CircularProgress,
  Stack,
  Autocomplete,
} from '@mui/material';
import type { SelectChangeEvent } from '@mui/material/Select';
import { useCheckTermInvoices, useGenerateTermInvoices } from '../../hooks/useInvoices';
import BulkRunReport from './BulkRunReport';
import { apiErrorMessage } from '../../utils/apiError';
import { useTermDiscountEligibility, useVariableDiscountSettings } from '../../hooks/useDiscounts';
import { useProductCategories } from '../../hooks/useProducts';
import { coverageFromSchoolDefault, coverageSuffix, describeCoverage } from '../../utils/discountCoverage';
import { Term } from '../../api/admin.api';
import { Family } from '../../api/users.api';
import { GenerateInvoicesResult, GenerateTermInvoiceInput } from '../../api/invoices.api';
import { useDefaultDueDateField } from './useDefaultDueDateField';
import LocalOfferIcon from '@mui/icons-material/LocalOffer';

interface GenerateInvoicesDialogProps {
  open: boolean;
  onClose: () => void;
  terms: Term[];
  families: Family[];
}

type ParentContact = {
  contact1FirstName?: string | null;
  contact1LastName?: string | null;
  contact2FirstName?: string | null;
  contact2LastName?: string | null;
};

// The list endpoint may serialize parents under either `parents` or `Parent`,
// depending on Prisma relation casing — read from whichever is present.
function getFamilyParents(family: Family): ParentContact[] {
  const raw = (family as unknown as { parents?: ParentContact[]; Parent?: ParentContact[] });
  return raw.parents ?? raw.Parent ?? [];
}

// Collect the unique full names attached to a family. A secondary contact who
// also has their own Parent row would otherwise appear twice: once as
// contact1* on their own record, once as contact2* on the primary's record.
function getUniqueFamilyContactNames(family: Family): string[] {
  const seen = new Set<string>();
  const names: string[] = [];
  const add = (first?: string | null, last?: string | null) => {
    const full = `${first ?? ''} ${last ?? ''}`.replace(/\s+/g, ' ').trim();
    if (!full) return;
    const key = full.toLowerCase();
    if (seen.has(key)) return;
    seen.add(key);
    names.push(full);
  };
  for (const p of getFamilyParents(family)) {
    add(p.contact1FirstName, p.contact1LastName);
    add(p.contact2FirstName, p.contact2LastName);
  }
  return names;
}

function getFamilySearchTokens(family: Family): string[] {
  const tokens = new Set<string>([family.name.toLowerCase()]);
  for (const fullName of getUniqueFamilyContactNames(family)) {
    const lower = fullName.toLowerCase();
    tokens.add(lower);
    for (const part of lower.split(' ')) {
      if (part) tokens.add(part);
    }
  }
  return [...tokens];
}

function getFamilyContactLabels(family: Family): string {
  return getUniqueFamilyContactNames(family).join(', ');
}

export default function GenerateInvoicesDialog({
  open,
  onClose,
  terms,
  families,
}: GenerateInvoicesDialogProps) {
  const currentYear = new Date().getFullYear();

  const [termId, setTermId] = useState('');
  const [selectedFamilyIds, setSelectedFamilyIds] = useState<string[]>([]);
  const [generateForAll, setGenerateForAll] = useState(true);
  // Prefilled with the school's default (#243); a typed date is kept.
  const { dueDate, changeDueDate, resetDueDate, helperText: dueDateHelperText, isError: dueDateDefaultFailed } =
    useDefaultDueDateField(open);
  const [groupRate, setGroupRate] = useState('');
  const [individualRate, setIndividualRate] = useState('');
  const [result, setResult] = useState<GenerateInvoicesResult | null>(null);
  const [selectedYear, setSelectedYear] = useState<number>(currentYear);
  const [hidePastTerms, setHidePastTerms] = useState(true);
  const [useCustomPricing, setUseCustomPricing] = useState(false);
  const [selectedDiscounts, setSelectedDiscounts] = useState<{
    [familyId: string]: {
      familyDiscount?: boolean;
      customDiscounts?: string[];
      studentDiscounts?: {
        [studentId: string]: {
          instrumentDiscount?: boolean;
          programLevelDiscount?: boolean;
          customDiscounts?: string[];
        };
      };
    };
  }>({});

  const generateMutation = useGenerateTermInvoices();
  // Check first (#376): the same request, nothing created. Cleared whenever an input changes, so a
  // shown check always describes the form as it is now. It never gates Generate.
  const checkMutation = useCheckTermInvoices();
  const resetCheck = checkMutation.reset;
  useEffect(() => {
    resetCheck();
  }, [termId, generateForAll, selectedFamilyIds, dueDate, groupRate, individualRate, useCustomPricing, selectedDiscounts, resetCheck]);
  const { data: discountEligibility, isLoading: isLoadingEligibility, isSuccess: eligibilityLoaded } =
    useTermDiscountEligibility(termId);
  // #625 D9: the automatic discounts cover the school default; the payload labels only the
  // catalogue entries (/qa phase 4 I2), so these are worded here. Asked only once the payload
  // loaded: both routes share its permission (operations_pricing:read).
  const discountSettings = useVariableDiscountSettings({ enabled: eligibilityLoaded });
  const productCategories = useProductCategories({ enabled: eligibilityLoaded });
  const automaticCovers = useMemo(
    () =>
      discountSettings.isSuccess && productCategories.isSuccess
        ? describeCoverage(
            coverageFromSchoolDefault(discountSettings.data),
            new Map(productCategories.data.map((c) => [c.id, c.name]))
          )
        : null,
    [discountSettings.isSuccess, discountSettings.data, productCategories.isSuccess, productCategories.data]
  );

  // Get unique years from terms
  const availableYears = Array.from(
    new Set(terms.map((term) => new Date(term.startDate).getFullYear()))
  ).sort((a, b) => b - a); // Sort descending (newest first)

  // Ensure current year is in the list even if no terms exist for it
  if (!availableYears.includes(currentYear)) {
    availableYears.unshift(currentYear);
  }

  // Filter terms based on selected year and hide past terms toggle, sorted ascending by start date
  const filteredTerms = terms
    .filter((term) => {
      const termYear = new Date(term.startDate).getFullYear();
      if (termYear !== selectedYear) return false;

      if (hidePastTerms) {
        const termEndDate = new Date(term.endDate);
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        return termEndDate >= today;
      }

      return true;
    })
    .sort((a, b) => new Date(a.startDate).getTime() - new Date(b.startDate).getTime());

  /** The ONE request both buttons send, so Check first describes exactly the run Generate makes. */
  const buildPayload = (): GenerateTermInvoiceInput => ({
    termId,
    familyIds: generateForAll ? undefined : selectedFamilyIds,
    // Empty → omitted: the server applies the school's default (#243).
    dueDate: dueDate || undefined,
    groupRate: groupRate ? parseFloat(groupRate) : undefined,
    individualRate: individualRate ? parseFloat(individualRate) : undefined,
    discountSelections: selectedDiscounts,
  });

  const handleCheck = async () => {
    if (!termId) return;
    try {
      await checkMutation.mutateAsync(buildPayload());
    } catch {
      // Shown below from checkMutation.error
    }
  };

  const handleGenerate = async () => {
    if (!termId) return;

    try {
      const generationResult = await generateMutation.mutateAsync(buildPayload());

      // Show result summary instead of closing immediately
      if (generationResult) {
        setResult(generationResult);
      }
    } catch {
      // Error is handled by the mutation's onError callback
    }
  };

  const handleClose = () => {
    setTermId('');
    setSelectedFamilyIds([]);
    setGenerateForAll(true);
    resetDueDate();
    setGroupRate('');
    setIndividualRate('');
    setResult(null);
    setSelectedYear(currentYear);
    setHidePastTerms(true);
    setUseCustomPricing(false);
    setSelectedDiscounts({});
    generateMutation.reset(); // Reset mutation state
    checkMutation.reset();
    onClose();
  };

  // Toggle discount selection for a family
  const handleToggleFamilyDiscount = (familyId: string, discountType: 'family' | string) => {
    setSelectedDiscounts((prev) => {
      const familyDiscounts = prev[familyId] || {};

      if (discountType === 'family') {
        return {
          ...prev,
          [familyId]: {
            ...familyDiscounts,
            familyDiscount: !familyDiscounts.familyDiscount,
          },
        };
      } else {
        // Custom discount
        const customDiscounts = familyDiscounts.customDiscounts || [];
        const isSelected = customDiscounts.includes(discountType);

        return {
          ...prev,
          [familyId]: {
            ...familyDiscounts,
            customDiscounts: isSelected
              ? customDiscounts.filter((id) => id !== discountType)
              : [...customDiscounts, discountType],
          },
        };
      }
    });
  };

  // Toggle discount selection for a student
  const handleToggleStudentDiscount = (
    familyId: string,
    studentId: string,
    discountType: 'instrument' | 'programLevel' | string
  ) => {
    setSelectedDiscounts((prev) => {
      const familyDiscounts = prev[familyId] || {};
      const studentDiscounts = familyDiscounts.studentDiscounts || {};
      const thisStudentDiscounts = studentDiscounts[studentId] || {};

      if (discountType === 'instrument') {
        return {
          ...prev,
          [familyId]: {
            ...familyDiscounts,
            studentDiscounts: {
              ...studentDiscounts,
              [studentId]: {
                ...thisStudentDiscounts,
                instrumentDiscount: !thisStudentDiscounts.instrumentDiscount,
              },
            },
          },
        };
      } else if (discountType === 'programLevel') {
        return {
          ...prev,
          [familyId]: {
            ...familyDiscounts,
            studentDiscounts: {
              ...studentDiscounts,
              [studentId]: {
                ...thisStudentDiscounts,
                programLevelDiscount: !thisStudentDiscounts.programLevelDiscount,
              },
            },
          },
        };
      } else {
        // Custom discount
        const customDiscounts = thisStudentDiscounts.customDiscounts || [];
        const isSelected = customDiscounts.includes(discountType);

        return {
          ...prev,
          [familyId]: {
            ...familyDiscounts,
            studentDiscounts: {
              ...studentDiscounts,
              [studentId]: {
                ...thisStudentDiscounts,
                customDiscounts: isSelected
                  ? customDiscounts.filter((id) => id !== discountType)
                  : [...customDiscounts, discountType],
              },
            },
          },
        };
      }
    });
  };

  // Helper to get family name by ID
  const getFamilyName = (familyId: string) => {
    const family = families.find((f) => f.id === familyId);
    return family?.name || familyId;
  };

  // Show results view after generation
  if (result) {
    return (
      // The results are the only record of the run (W1): only the Close button closes them,
      // never a stray backdrop click or Escape.
      <Dialog
        open={open}
        onClose={(_event, reason) => {
          if (reason !== 'backdropClick' && reason !== 'escapeKeyDown') handleClose();
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Invoice Generation Results</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 1 }}>
            <BulkRunReport mode="result" report={result} getFamilyName={getFamilyName} />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose} variant="contained">
            Close
          </Button>
        </DialogActions>
      </Dialog>
    );
  }

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>Generate Term Invoices</DialogTitle>
      <DialogContent>
        <Box sx={{ pt: 1, display: 'flex', flexDirection: 'column', gap: 2 }}>
          <Alert severity="info" sx={{ mb: 1 }}>
            Generate invoices for all enrolled students in a term. Invoices will
            be created based on lesson enrollments with appropriate pricing for
            hybrid lessons.
          </Alert>

          {/* Year Filter and Hide Past Terms */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
            <FormControl size="small" sx={{ minWidth: 100 }}>
              <InputLabel>Year</InputLabel>
              <Select
                value={selectedYear}
                label="Year"
                onChange={(e: SelectChangeEvent<number>) => {
                  setSelectedYear(e.target.value as number);
                  setTermId(''); // Clear selected term when year changes
                }}
              >
                {availableYears.map((year) => (
                  <MenuItem key={year} value={year}>
                    {year}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControlLabel
              control={
                <Switch
                  checked={hidePastTerms}
                  onChange={(e) => {
                    setHidePastTerms(e.target.checked);
                    setTermId(''); // Clear selected term when filter changes
                  }}
                  size="small"
                />
              }
              label="Hide past terms"
            />

            <Chip
              label={`Showing ${filteredTerms.length} of ${terms.filter((t) => new Date(t.startDate).getFullYear() === selectedYear).length} terms`}
              size="small"
              variant="outlined"
            />
          </Box>

          {/* Term Selection */}
          <FormControl fullWidth required>
            <InputLabel>Term</InputLabel>
            <Select
              value={termId}
              label="Term"
              onChange={(e: SelectChangeEvent) => setTermId(e.target.value)}
            >
              {filteredTerms.map((term) => (
                <MenuItem key={term.id} value={term.id}>
                  {term.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {/* Discount Preview Section */}
          {termId && (
            <Box sx={{ mt: 2 }}>
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                <LocalOfferIcon sx={{ fontSize: 16, mr: 0.5, verticalAlign: 'text-bottom' }} />
                Available Discounts
              </Typography>

              {isLoadingEligibility ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
                  <CircularProgress size={24} />
                </Box>
              ) : discountEligibility && discountEligibility.length > 0 ? (
                <Box
                  sx={{
                    maxHeight: 300,
                    overflowY: 'auto',
                    border: '1px solid',
                    borderColor: 'divider',
                    borderRadius: 1,
                    p: 2,
                  }}
                >
                  {discountEligibility.map((family: any) => {
                    const familyDiscountState = selectedDiscounts[family.familyId] || {};
                    const hasAnyDiscounts =
                      family.variableDiscounts?.familyDiscount ||
                      (family.variableDiscounts?.instrumentDiscounts?.length > 0) ||
                      (family.variableDiscounts?.programLevelDiscounts?.length > 0) ||
                      (family.customDiscounts?.family?.length > 0) ||
                      (family.customDiscounts?.student?.length > 0);

                    if (!hasAnyDiscounts) return null;

                    return (
                      <Box
                        key={family.familyId}
                        sx={{
                          mb: 2,
                          pb: 2,
                          borderBottom: '1px solid',
                          borderColor: 'divider',
                          '&:last-child': { borderBottom: 'none', mb: 0, pb: 0 },
                        }}
                      >
                        <Typography variant="subtitle2" sx={{ mb: 1 }}>
                          {family.familyName} ({family.studentCount} student
                          {family.studentCount !== 1 ? 's' : ''})
                        </Typography>

                        <Stack spacing={1}>
                          {/* Family Variable Discount */}
                          {family.variableDiscounts?.familyDiscount && (
                            <FormControlLabel
                              control={
                                <Checkbox
                                  checked={!!familyDiscountState.familyDiscount}
                                  onChange={() =>
                                    handleToggleFamilyDiscount(family.familyId, 'family')
                                  }
                                  size="small"
                                />
                              }
                              label={
                                <Typography variant="body2">
                                  Family Discount ({family.variableDiscounts.familyDiscount.percent}
                                  %{coverageSuffix(automaticCovers)})
                                </Typography>
                              }
                            />
                          )}

                          {/* Family Custom Discounts */}
                          {family.customDiscounts?.family?.map((discount: any) => (
                            <FormControlLabel
                              key={discount.id}
                              control={
                                <Checkbox
                                  checked={
                                    familyDiscountState.customDiscounts?.includes(discount.id) ||
                                    false
                                  }
                                  onChange={() =>
                                    handleToggleFamilyDiscount(family.familyId, discount.id)
                                  }
                                  size="small"
                                />
                              }
                              label={
                                <Typography variant="body2">
                                  {discount.name} (
                                  {discount.isPercent ? `${discount.value}%` : `$${discount.value}`}
                                  {coverageSuffix(discount.coverageLabel)})
                                </Typography>
                              }
                            />
                          ))}

                          {/* Student Variable Discounts */}
                          {family.variableDiscounts?.instrumentDiscounts?.map((studentDiscount: any) => (
                            <FormControlLabel
                              key={`${studentDiscount.studentId}-instrument`}
                              control={
                                <Checkbox
                                  checked={
                                    familyDiscountState.studentDiscounts?.[studentDiscount.studentId]
                                      ?.instrumentDiscount || false
                                  }
                                  onChange={() =>
                                    handleToggleStudentDiscount(
                                      family.familyId,
                                      studentDiscount.studentId,
                                      'instrument'
                                    )
                                  }
                                  size="small"
                                />
                              }
                              label={
                                <Typography variant="body2">
                                  {studentDiscount.studentName} - Instrument Discount (
                                  {studentDiscount.percent}%, {studentDiscount.instrumentCount}{' '}
                                  instruments{coverageSuffix(automaticCovers)})
                                </Typography>
                              }
                            />
                          ))}

                          {family.variableDiscounts?.programLevelDiscounts?.map((studentDiscount: any) => (
                            <FormControlLabel
                              key={`${studentDiscount.studentId}-programLevel`}
                              control={
                                <Checkbox
                                  checked={
                                    familyDiscountState.studentDiscounts?.[studentDiscount.studentId]
                                      ?.programLevelDiscount || false
                                  }
                                  onChange={() =>
                                    handleToggleStudentDiscount(
                                      family.familyId,
                                      studentDiscount.studentId,
                                      'programLevel'
                                    )
                                  }
                                  size="small"
                                />
                              }
                              label={
                                <Typography variant="body2">
                                  {studentDiscount.studentName} - Program Level Discount (
                                  {studentDiscount.percent}%, {studentDiscount.programLevelCount}{' '}
                                  levels{coverageSuffix(automaticCovers)})
                                </Typography>
                              }
                            />
                          ))}

                          {/* Student Custom Discounts */}
                          {family.customDiscounts?.student?.map((discount: any) => {
                            // Backend returns students array with studentId and studentName
                            return discount.students?.map((student: any) => {
                              return (
                                <FormControlLabel
                                  key={`${discount.id}-${student.studentId}`}
                                  control={
                                    <Checkbox
                                      checked={
                                        familyDiscountState.studentDiscounts?.[student.studentId]
                                          ?.customDiscounts?.includes(discount.id) || false
                                      }
                                      onChange={() =>
                                        handleToggleStudentDiscount(
                                          family.familyId,
                                          student.studentId,
                                          discount.id
                                        )
                                      }
                                      size="small"
                                    />
                                  }
                                  label={
                                    <Typography variant="body2">
                                      {student.studentName} - {discount.name} (
                                      {discount.isPercent ? `${discount.value}%` : `$${discount.value}`}
                                      {coverageSuffix(discount.coverageLabel)})
                                    </Typography>
                                  }
                                />
                              );
                            });
                          })}
                        </Stack>
                      </Box>
                    );
                  })}
                </Box>
              ) : termId && !isLoadingEligibility ? (
                <Alert severity="info" sx={{ mt: 1 }}>
                  No families are eligible for discounts in this term.
                </Alert>
              ) : null}
            </Box>
          )}

          {/* Family Selection */}
          <FormControlLabel
            control={
              <Checkbox
                checked={generateForAll}
                onChange={(e) => setGenerateForAll(e.target.checked)}
              />
            }
            label="Generate for all families with enrolments"
          />

          {!generateForAll && (
            <Box>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                Select Families
              </Typography>
              <Autocomplete
                multiple
                options={families}
                getOptionLabel={(option) => option.name}
                isOptionEqualToValue={(option, value) => option.id === value.id}
                value={families.filter((f) => selectedFamilyIds.includes(f.id))}
                onChange={(_, newValue) =>
                  setSelectedFamilyIds(newValue.map((f) => f.id))
                }
                filterSelectedOptions
                filterOptions={(options, { inputValue }) => {
                  const query = inputValue.trim().toLowerCase();
                  if (!query) return options;
                  return options.filter((family) =>
                    getFamilySearchTokens(family).some((token) =>
                      token.includes(query)
                    )
                  );
                }}
                renderOption={(props, option) => {
                  const contacts = getFamilyContactLabels(option);
                  return (
                    <li {...props} key={option.id}>
                      <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                        <Typography variant="body2">{option.name}</Typography>
                        {contacts && (
                          <Typography variant="caption" color="text.secondary">
                            {contacts}
                          </Typography>
                        )}
                      </Box>
                    </li>
                  );
                }}
                renderTags={(value, getTagProps) =>
                  value.map((option, index) => (
                    <Chip
                      label={option.name}
                      color="primary"
                      {...getTagProps({ index })}
                      key={option.id}
                    />
                  ))
                }
                renderInput={(params) => (
                  <TextField
                    {...params}
                    placeholder={
                      selectedFamilyIds.length === 0
                        ? `Search by family or contact name (${families.length} total)`
                        : ''
                    }
                    size="small"
                  />
                )}
                ListboxProps={{ style: { maxHeight: 300 } }}
              />
              {selectedFamilyIds.length > 0 && (
                <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                  {selectedFamilyIds.length} family/families selected
                </Typography>
              )}
            </Box>
          )}

          {/* Due Date */}
          <TextField
            fullWidth
            type="date"
            label="Due Date"
            value={dueDate}
            onChange={(e) => changeDueDate(e.target.value)}
            InputLabelProps={{ shrink: true }}
            error={dueDateDefaultFailed && !dueDate}
            helperText={dueDateHelperText}
          />

          {/* Custom Pricing Toggle */}
          <FormControlLabel
            control={
              <Switch
                checked={useCustomPricing}
                onChange={(e) => {
                  setUseCustomPricing(e.target.checked);
                  if (!e.target.checked) {
                    // Clear custom rates when turning off
                    setGroupRate('');
                    setIndividualRate('');
                  }
                }}
                size="small"
              />
            }
            label="Use custom pricing (override pricing matrix)"
          />

          {/* Custom Pricing Fields */}
          {useCustomPricing && (
            <Grid container spacing={2}>
              <Grid item xs={6}>
                <TextField
                  fullWidth
                  type="number"
                  label="Group Rate ($/week)"
                  value={groupRate}
                  onChange={(e) => setGroupRate(e.target.value)}
                  inputProps={{ min: 0, step: 0.01 }}
                  helperText="Overrides pricing matrix"
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  fullWidth
                  type="number"
                  label="Individual Rate ($/week)"
                  value={individualRate}
                  onChange={(e) => setIndividualRate(e.target.value)}
                  inputProps={{ min: 0, step: 0.01 }}
                  helperText="Overrides pricing matrix"
                />
              </Grid>
            </Grid>
          )}

          {/* Check first's answer (#376): a prediction of the run, never a guarantee. */}
          {checkMutation.data && (
            <Box sx={{ border: 1, borderColor: 'divider', borderRadius: 1, p: 1.5 }}>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                Check: {checkMutation.data.termName}
              </Typography>
              <BulkRunReport mode="check" report={checkMutation.data} getFamilyName={getFamilyName} />
            </Box>
          )}
          {checkMutation.isError && (
            <Alert severity="error">{apiErrorMessage(checkMutation.error, 'Could not check this run')}</Alert>
          )}

          {/* A missing price no longer stops the run (#376): it holds back that family and is listed in
              the results. What is left here is a real failure (a bad term, a server error). */}
          {generateMutation.isError && (
            <Alert severity="error">
              {apiErrorMessage(generateMutation.error, 'Failed to generate invoices')}
            </Alert>
          )}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={generateMutation.isPending}>
          Cancel
        </Button>
        <Button
          variant="outlined"
          onClick={handleCheck}
          disabled={
            !termId ||
            (!generateForAll && selectedFamilyIds.length === 0) ||
            checkMutation.isPending ||
            generateMutation.isPending
          }
          startIcon={checkMutation.isPending ? <CircularProgress size={16} color="inherit" /> : undefined}
        >
          {checkMutation.isPending ? 'Checking...' : 'Check first'}
        </Button>
        <Button
          variant="contained"
          onClick={handleGenerate}
          disabled={
            !termId ||
            (!generateForAll && selectedFamilyIds.length === 0) ||
            generateMutation.isPending
          }
          startIcon={
            generateMutation.isPending ? (
              <CircularProgress size={16} color="inherit" />
            ) : undefined
          }
        >
          {generateMutation.isPending ? 'Generating...' : 'Generate Invoices'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
